<template>
	<v-dialog :value="dialog" max-width="640px" @input="onDialogInput">
		<v-card>
			<v-toolbar flat dense>
				<v-toolbar-title class="subheading">Start a form</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon flat @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-card-text>
				<v-text-field
					v-if="forms.length > 3"
					v-model="search"
					append-icon="search"
					label="Search forms"
					single-line
					hide-details
					class="mb-3"
				/>

				<div v-if="groupedForms.length === 0" class="text-xs-center py-4">
					<div class="subheading mb-1">No forms match your search</div>
					<div class="text--secondary">Try a different form name or category.</div>
				</div>

				<v-list v-else class="self-assignable-form-dialog__list text-xs-left" two-line>
					<template v-for="group in groupedForms">
						<v-subheader :key="`${group.key}-header`">{{ group.name }}</v-subheader>
						<template v-for="(form, index) in group.forms">
							<v-list-tile :key="form.id" class="self-assignable-form-dialog__tile">
								<v-list-tile-action>
									<v-icon color="info">mdi-form-select</v-icon>
								</v-list-tile-action>
								<div class="self-assignable-form-dialog__content">
									<div class="self-assignable-form-dialog__title">{{ form.name }}</div>
									<div class="self-assignable-form-dialog__description text--secondary">
										{{ form.description || 'No description provided.' }}
									</div>
								</div>
								<div class="self-assignable-form-dialog__action">
									<v-btn small color="primary" :loading="isStartingForm(form)" :disabled="isStarting" @click.stop="start(form)">
										Start
									</v-btn>
								</div>
							</v-list-tile>
							<v-divider v-if="index < group.forms.length - 1" :key="`${form.id}-divider`" inset />
						</template>
					</template>
				</v-list>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'SelfAssignableFormDialog',
	props: {
		dialog: {
			type: Boolean,
			default: false
		},
		forms: {
			type: Array,
			default: () => []
		},
		isStarting: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			search: '',
			startingFormId: null
		};
	},
	computed: {
		normalisedSearch() {
			return this.search.trim().toLowerCase();
		},
		visibleForms() {
			const forms = this.forms
				.filter(form => form && form.id && form.status === 'Active')
				.slice()
				.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

			if (!this.normalisedSearch) return forms;

			return forms.filter(form => [
				form.name,
				form.description,
				form.categoryName || 'Uncategorised'
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
				.includes(this.normalisedSearch));
		},
		groupedForms() {
			const groupsByKey = {};

			this.visibleForms.forEach(form => {
				const key = form.categoryId || form.categoryName || '__uncategorised';
				if (!groupsByKey[key]) {
					groupsByKey[key] = {
						key,
						name: form.categoryName || 'Uncategorised',
						forms: []
					};
				}

				groupsByKey[key].forms.push(form);
			});

			return Object.values(groupsByKey).sort((a, b) => {
				if (a.key === '__uncategorised') return 1;
				if (b.key === '__uncategorised') return -1;
				return a.name.localeCompare(b.name);
			});
		}
	},
	watch: {
		dialog(value) {
			if (value) {
				this.search = '';
				this.startingFormId = null;
			}
		},
		isStarting(value) {
			if (!value) {
				this.startingFormId = null;
			}
		}
	},
	methods: {
		onDialogInput(value) {
			if (!value) this.close();
		},
		close() {
			if (this.isStarting) return;
			this.$emit('close');
		},
		start(form) {
			if (!form || !form.id || this.isStarting) return;
			this.startingFormId = form.id;
			this.$emit('start', form);
		},
		isStartingForm(form) {
			return this.isStarting && form && form.id === this.startingFormId;
		}
	}
};
</script>

<style scoped>
.self-assignable-form-dialog__list {
	max-height: 420px;
	overflow-y: auto;
}

.self-assignable-form-dialog__tile {
	height: auto;
	min-height: 72px;
	align-items: center;
	cursor: default;
}

.self-assignable-form-dialog__content {
	flex: 1 1 auto;
	min-width: 0;
	padding: 10px 16px 10px 0;
}

.self-assignable-form-dialog__title {
	color: rgba(0, 0, 0, 0.87);
	font-size: 14px;
	font-weight: 500;
	line-height: 1.35;
	margin-bottom: 2px;
	overflow-wrap: break-word;
	white-space: normal;
}

.self-assignable-form-dialog__description {
	font-size: 13px;
	line-height: 1.35;
	overflow-wrap: break-word;
	white-space: normal;
}

.self-assignable-form-dialog__action {
	flex: 0 0 88px;
	display: flex;
	justify-content: flex-end;
}

@media (max-width: 480px) {
	.self-assignable-form-dialog__tile {
		flex-wrap: wrap;
	}

	.self-assignable-form-dialog__action {
		flex-basis: 100%;
		justify-content: flex-start;
		padding: 0 0 8px 56px;
	}
}
</style>
